import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Translate } from '../../../../../shared/LanguageService';
import { useWindowFillingArea } from './useWindowFillingArea';

const SPACING = '16px';
const PANEL_WIDTH = '485px';

type OrderTotal = {
    amount: number;
    currencySign: string;
};

export type Presentation = {
    label: string;
    content: React.ReactNode;
};

type CreateOrderLayoutProps = {
    notice: React.ReactNode;
    title: string;
    stepNumber: number | null;
    numberOfSteps: number;
    presentation: Presentation | null;
    total: OrderTotal | null;
    canContinue: boolean;
    nextLabel: string;
    onBack: (() => void) | null;
    onNext: () => void;
    children: React.ReactNode;
};

export function CreateOrderLayout({
    notice,
    title,
    stepNumber,
    numberOfSteps,
    presentation,
    total,
    canContinue,
    nextLabel,
    onBack,
    onNext,
    children
}: CreateOrderLayoutProps) {
    let theme = useTheme();
    let isSmallerScreen = useMediaQuery(theme.breakpoints.down('sm'));
    let { anchorRef, area } = useWindowFillingArea(!isSmallerScreen);

    let wideScreenGrid = {
        gridTemplateColumns: `minmax(0, 1fr) ${PANEL_WIDTH}`,
        gridTemplateRows: notice ? 'auto minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto',
        gridTemplateAreas: notice
            ? '"notice ." "presentation panel" "footer footer"'
            : '"presentation panel" "footer footer"'
    };
    let smallScreenGrid = {
        gridTemplateColumns: 'minmax(0, 1fr)',
        gridTemplateAreas: notice ? '"notice" "panel" "footer"' : '"panel" "footer"'
    };
    let size = area
        ? {
              height: `${area.height - 16}px`,
              width: `${area.width}px`,
              marginLeft: `${area.marginLeft}px`
          }
        : { minHeight: 'calc(100vh - 160px)' };

    return (
        <div ref={anchorRef}>
            <Box
                sx={{
                    display: 'grid',
                    rowGap: SPACING,
                    marginTop: SPACING,
                    ...(isSmallerScreen ? smallScreenGrid : wideScreenGrid),
                    ...size
                }}
            >
                {notice && (
                    <Box sx={{ gridArea: 'notice', '& .MuiAlert-root': { margin: 0 } }}>
                        {notice}
                    </Box>
                )}
                {!isSmallerScreen && (
                    <Box
                        component="aside"
                        aria-label={presentation ? presentation.label : undefined}
                        sx={{
                            gridArea: 'presentation',
                            minHeight: 0,
                            display: 'flex',
                            overflow: 'auto',
                            backgroundColor: 'primary.dark',
                            color: 'primary.contrastText'
                        }}
                    >
                        {presentation && presentation.content}
                    </Box>
                )}
                <Box
                    sx={{
                        gridArea: 'panel',
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'background.paper'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            padding: `0 ${SPACING} ${SPACING}`
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{ margin: 0, fontSize: '24px', fontWeight: 'bold', lineHeight: 1 }}
                        >
                            {title}
                        </Typography>
                        {stepNumber !== null && (
                            <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                {Translate('order/create/v2/step')}{' '}
                                <span>
                                    {stepNumber}/{numberOfSteps}
                                </span>
                            </Typography>
                        )}
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            padding: `0 ${SPACING} ${SPACING}`,
                            overflowY: area ? 'auto' : 'visible'
                        }}
                    >
                        {children}
                    </Box>
                </Box>
                <Box
                    sx={{
                        gridArea: 'footer',
                        position: area ? 'static' : 'sticky',
                        bottom: 0,
                        zIndex: 'appBar',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        borderTop: 1,
                        borderColor: 'divider',
                        backgroundColor: 'background.paper'
                    }}
                >
                    <Box
                        sx={{
                            width: isSmallerScreen ? '100%' : PANEL_WIDTH,
                            display: 'flex',
                            alignItems: 'center',
                            gap: SPACING,
                            padding: SPACING
                        }}
                    >
                        {total && (
                            <Box>
                                <Typography sx={{ fontSize: '14px' }}>
                                    {Translate('order/create/v2/total')}
                                </Typography>
                                <Typography
                                    data-testid="order-total"
                                    sx={{ fontSize: '24px', fontWeight: 'bold' }}
                                >
                                    {total.amount}
                                    {total.currencySign}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ marginLeft: 'auto', display: 'flex', gap: SPACING }}>
                            {onBack && (
                                <Button variant="outlined" onClick={onBack}>
                                    {Translate('order/create/v2/button/back')}
                                </Button>
                            )}
                            <Button variant="contained" disabled={!canContinue} onClick={onNext}>
                                {nextLabel}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </div>
    );
}
